import React, { useState, useEffect } from 'react';
import ReactECharts from 'echarts-for-react';
import { Text, Flex, Badge } from '@chakra-ui/react';
import ChartCard from './ChartCard';
import { appAnalyticsService } from '../../services/appAnalyticsService';
import { CHART_COLORS } from '../../utils/chartColors';

interface ModuleUsageChartProps {
  projectId?: string;
  businessId?: string;
}

const FONT_FAMILY = 'TT Interphases Pro, Inter, sans-serif';

interface ModuleData {
  name: string;
  count: number;
}

const ModuleUsageChart: React.FC<ModuleUsageChartProps> = ({
  projectId,
  businessId,
}) => {
  const [modules, setModules] = useState<ModuleData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasData, setHasData] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        if (!projectId && !businessId) {
          setHasData(false);
          return;
        }

        const response = await appAnalyticsService.getInteractionDistribution({
          projectId,
          businessId,
        });

        if (response && response.byType && Array.isArray(response.byType)) {
          const moduleData: ModuleData[] = response.byType.map((item: any) => ({
            name: item.type || item.name || 'Unknown',
            count: item.count || 0,
          }));
          setModules(moduleData);
          setHasData(moduleData.length > 0);
        } else {
          setHasData(false);
        }
      } catch (err: any) {
        console.error('[ModuleUsageChart] Error fetching data:', err);
        // Don't show error for missing data - just show placeholder
        setHasData(false);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [projectId, businessId]);

  const getChartOption = () => {
    if (modules.length === 0) return {};

    const names = modules.map((m) => m.name.replace(/_/g, ' '));
    const counts = modules.map((m) => m.count);

    return {
      tooltip: {
        trigger: 'axis' as const,
        axisPointer: { type: 'shadow' as const },
        textStyle: { fontFamily: FONT_FAMILY },
        formatter: (params: any) => {
          const p = params[0];
          return `<b>${p.name}</b><br/>Interactions: ${p.value}`;
        },
      },
      grid: {
        left: 50,
        right: 20,
        bottom: 120,
        top: 30,
      },
      xAxis: {
        type: 'category' as const,
        data: names,
        axisLabel: {
          fontFamily: FONT_FAMILY,
          fontSize: 10,
          color: '#666',
          interval: 0,
          rotate: 45,
          align: 'right' as const,
        },
        axisLine: { lineStyle: { color: '#E2E8F0' } },
        axisTick: { alignWithLabel: true },
      },
      yAxis: {
        type: 'value' as const,
        name: 'No. of Interactions',
        nameTextStyle: { fontFamily: FONT_FAMILY, fontSize: 11, color: '#999' },
        axisLabel: {
          fontFamily: FONT_FAMILY,
          fontSize: 11,
          color: '#999',
        },
        splitLine: { lineStyle: { color: '#EDF2F7' } },
      },
      series: [
        {
          type: 'bar',
          data: counts.map((count, idx) => ({
            value: count,
            itemStyle: {
              color: CHART_COLORS.palette[idx % CHART_COLORS.palette.length],
              borderRadius: [4, 4, 0, 0],
            },
          })),
          barWidth: '45%',
          label: {
            show: true,
            position: 'top',
            fontFamily: FONT_FAMILY,
            fontSize: 11,
          },
        },
      ],
    };
  };

  return (
    <ChartCard
      title="Module Usage"
      subtitle="Interaction distribution by module type"
      isLoading={isLoading}
      error={error}
    >
      {!hasData && !isLoading ? (
        <Flex justify="center" align="center" minH="200px" direction="column" gap={2}>
          <Badge colorScheme="gray" fontSize="sm" px={3} py={1} borderRadius="md">
            Coming Soon
          </Badge>
          <Text color="gray.400" fontSize="sm">
            Module usage data is not yet available
          </Text>
        </Flex>
      ) : (
        <ReactECharts
          option={getChartOption()}
          style={{ height: '380px', width: '100%' }}
          opts={{ renderer: 'canvas' }}
        />
      )}
    </ChartCard>
  );
};

export default ModuleUsageChart;
